import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, BadgeCheck, Bell, CalendarClock, CheckCircle2, Database,
  HardDrive, Loader2, PauseCircle, Radio, ShieldCheck, Users, XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money, shortDate } from "@/lib/format";

const STATUS = {
  active: { label: "Active", icon: BadgeCheck, chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
            line: "Everything in the plan is switched on." },
  pending: { label: "Awaiting activation", icon: CalendarClock, chip: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
             line: "The licensing server has not enabled this tenant yet." },
  suspended: { label: "Suspended", icon: PauseCircle, chip: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400",
               line: "The licensing server has suspended this subscription." },
  expired: { label: "Expired", icon: XCircle, chip: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400",
             line: "This subscription has run past its end date." },
};

const NOTICE_TONE = {
  info: "border-primary/30 bg-primary/5 text-primary",
  billing: "border-[hsl(var(--teal))]/30 bg-[hsl(var(--teal))]/8 text-[hsl(var(--teal))]",
  warning: "border-amber-500/30 bg-amber-500/8 text-amber-600 dark:text-amber-400",
  action: "border-rose-500/30 bg-rose-500/8 text-rose-600 dark:text-rose-400",
};

const Meter = ({ label, used, limit, unit = "", pct, icon: Icon, testId }) => (
  <div className="rounded-xl border border-border p-3" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </span>
      <span className="font-mono text-[11.5px] text-foreground">
        {used}{unit} <span className="text-muted-foreground">/ {limit ?? "—"}{limit ? unit : ""}</span>
      </span>
    </div>
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className={`h-full rounded-full transition-[width] duration-700 ${
        pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-primary"}`}
           style={{ width: `${Math.min(100, pct || 0)}%` }} />
    </div>
    <div className="mt-1 text-[10.5px] text-muted-foreground">{pct || 0}% of the entitlement used</div>
  </div>
);

export default function Subscription() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setFailure("");
    try {
      const res = await api.get("/licence/subscription");
      setData(res.data);
    } catch (e) { setFailure(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const heartbeat = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/licence/heartbeat");
      toast[r.delivered ? "success" : "info"](r.detail);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const readAll = async () => {
    try {
      await api.post("/licence/notifications/read-all");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (failure) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Subscription" title="We could not read your licence"
                    description="Nothing was changed — this is only a reading screen." />
        <Panel title="Could not load" testId="subscription-failed">
          <div className="p-4">
            <p className="mb-3 text-[12.5px] text-muted-foreground" data-testid="subscription-failed-message">
              {failure}
            </p>
            <Button size="sm" onClick={load} data-testid="subscription-retry">Try again</Button>
          </div>
        </Panel>
      </div>
    );
  }

  if (!data) return <Loading label="Reading your licence and usage" />;

  const { licence, usage, sla, sla_rows: slaRows, modules, notifications } = data;
  const status = STATUS[licence.status] || STATUS.pending;
  const StatusIcon = status.icon;
  const manage = can("licence", "write");

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <PageHeader eyebrow="Licensing" title="Subscription and service levels"
                  description="What the licensing server has enabled for this charity, how much of it you are using, and the service you are promised."
                  actions={manage && (
                    <Button size="sm" onClick={heartbeat} disabled={busy}
                            data-testid="subscription-heartbeat">
                      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            : <Radio className="mr-1.5 h-3.5 w-3.5" />}
                      Send usage reading
                    </Button>
                  )} />

      <Panel testId="panel-subscription-status" kicker="Licence" title="Where this tenant stands">
        <div className="grid gap-4 p-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Chip className={status.chip} testId="subscription-status">
                <StatusIcon className="h-3 w-3" /> {status.label}
              </Chip>
              <Chip className="border-border bg-muted text-muted-foreground" testId="subscription-plan-chip">
                {licence.plan?.name}
              </Chip>
              <span className="font-mono text-[11px] text-muted-foreground" data-testid="subscription-licence-key">
                {licence.licence_key}
              </span>
            </div>
            <p className="text-[12.5px] text-muted-foreground" data-testid="subscription-status-line">
              {status.line} Module access and activation are controlled remotely by the licensing
              server; nothing on this screen changes what you may use.
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-3">
              {[["Tenant", licence.tenant_id],
                ["Billing", licence.plan?.billing_period],
                ["Annual fee", licence.plan?.price
                  ? money(licence.plan.price, licence.plan.currency || "INR", true) : "—"],
                ["Started", licence.starts_on ? shortDate(licence.starts_on) : "—"],
                ["Renews", licence.renews_on ? shortDate(licence.renews_on) : "—"],
                ["Last sync", licence.last_sync_at ? dateTime(licence.last_sync_at) : "never"],
                ["Last usage reading", licence.last_heartbeat_at ? dateTime(licence.last_heartbeat_at) : "never"],
                ["Licensing server", data.server.configured ? "connected" : "not configured yet"]]
                .map(([label, value]) => (
                  <div key={label}>
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="text-foreground">{value || "—"}</div>
                  </div>
                ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Kpi testId="subscription-kpi-seats" label="Seats in use" icon={Users}
                 value={`${usage.seats_used}/${usage.seats_limit ?? "—"}`}
                 sub={`${usage.seats_pct}% of the entitlement`} />
            <Kpi testId="subscription-kpi-modules" label="Modules enabled" icon={ShieldCheck}
                 accent="teal" value={`${data.modules_enabled}/${data.modules_total}`}
                 sub="switched on remotely" />
            <Kpi testId="subscription-kpi-uptime" label="Uptime this period" icon={CheckCircle2}
                 accent="emerald" value={`${data.measured.uptime_pct}%`}
                 sub={`target ${sla.uptime_target_pct}%`} />
            <Kpi testId="subscription-kpi-notices" label="Unread notices" icon={Bell}
                 accent={data.unread ? "amber" : "slate"} value={data.unread}
                 sub="from the licensing server" />
          </div>
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
        <Panel testId="panel-subscription-usage" kicker="Usage" title="What you are consuming"
               actions={<span className="text-[11px] text-muted-foreground">{usage.month}</span>}>
          <div className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Meter testId="usage-seats" label="Seats" icon={Users} used={usage.seats_used}
                     limit={usage.seats_limit} pct={usage.seats_pct} />
              <Meter testId="usage-storage" label="Storage" icon={HardDrive} unit=" GB"
                     used={usage.storage_gb} limit={usage.storage_limit_gb} pct={usage.storage_pct} />
              <Meter testId="usage-api" label="API calls" icon={Database} used={usage.api_calls_month}
                     limit={usage.api_limit_month} pct={usage.api_pct} />
            </div>
            <Table testId="usage-records-table"
                   head={<><Th>Records held</Th><Th className="text-right">Count</Th></>}>
              {usage.records.map((r) => (
                <tr key={r.module} data-testid={`usage-record-${r.module}`}>
                  <Td>{r.label}</Td>
                  <Td className="text-right font-mono text-[11.5px]">{r.count.toLocaleString()}</Td>
                </tr>
              ))}
              <tr>
                <Td className="font-semibold">Total</Td>
                <Td className="text-right font-mono text-[11.5px] font-semibold"
                    data-testid="usage-records-total">
                  {usage.records_total.toLocaleString()}
                </Td>
              </tr>
            </Table>
          </div>
        </Panel>

        <Panel testId="panel-subscription-sla" kicker="Service levels"
               title="What we promise and what you got">
          <div className="p-4">
            <Table testId="sla-table"
                   head={<><Th>Parameter</Th><Th>Target</Th><Th>This period</Th>
                     <Th className="text-right">Met</Th></>}>
              {slaRows.map((row) => (
                <tr key={row.parameter} data-testid={`sla-row-${row.parameter.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Td>{row.parameter}</Td>
                  <Td className="text-muted-foreground">{row.target}</Td>
                  <Td className="font-mono text-[11.5px]">{row.actual}</Td>
                  <Td className="text-right">
                    {row.met
                      ? <CheckCircle2 className="ml-auto h-3.5 w-3.5 text-emerald-500" />
                      : <AlertTriangle className="ml-auto h-3.5 w-3.5 text-rose-500" />}
                  </Td>
                </tr>
              ))}
            </Table>
            <div className="mt-3 space-y-1 text-[11.5px] text-muted-foreground">
              <div><span className="text-foreground">Support window:</span> {sla.support_window}</div>
              <div><span className="text-foreground">Service credits:</span> {sla.credits_policy}</div>
            </div>
          </div>
        </Panel>
      </div>

      <Panel testId="panel-subscription-modules" kicker="Entitlements"
             title="Modules the licensing server has switched on"
             actions={<span className="text-[11px] text-muted-foreground">
               {data.modules_enabled} of {data.modules_total} enabled
             </span>}>
        <div className="grid gap-2 p-4 sm:grid-cols-2 xl:grid-cols-3">
          {modules.map((m) => (
            <div key={m.key} data-testid={`module-${m.key}`}
                 className={`flex items-center gap-2 rounded-xl border p-2.5 ${
                   m.enabled ? "border-border bg-card" : "border-dashed border-border bg-muted/30 opacity-70"}`}>
              {m.enabled
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                : <XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-medium text-foreground">{m.label}</div>
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{m.area}</div>
              </div>
              <span className="ml-auto shrink-0 text-[10.5px] uppercase tracking-wider text-muted-foreground"
                    data-testid={`module-state-${m.key}`}>
                {m.enabled ? "on" : "off"}
              </span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel testId="panel-subscription-notices" kicker="Licensing server"
             title="Notifications"
             actions={data.unread > 0 && (
               <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                       onClick={readAll} data-testid="notices-read-all">
                 Mark all as read
               </Button>
             )}>
        <div className="space-y-2 p-4">
          {notifications.length === 0 && (
            <p className="py-6 text-center text-[12px] text-muted-foreground" data-testid="notices-empty">
              No notices from the licensing server yet.
            </p>
          )}
          {notifications.map((n) => (
            <div key={n.id} data-testid={`licence-notice-${n.id}`}
                 className={`rounded-xl border p-3 ${NOTICE_TONE[n.kind] || NOTICE_TONE.info} ${
                   n.read_at ? "opacity-70" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-semibold text-foreground">{n.title}</span>
                <span className="shrink-0 text-[10.5px] text-muted-foreground">{dateTime(n.created_at)}</span>
              </div>
              {n.body && <p className="mt-1 text-[12px] text-muted-foreground">{n.body}</p>}
              {!n.read_at && (
                <Chip className="mt-2 border-current bg-transparent" testId={`notice-unread-${n.id}`}>
                  <Bell className="h-3 w-3" /> new
                </Chip>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
